import { useEffect, useRef } from 'react';
import { authApiV1 } from '../axiosWrapper';
import { 
    trackExceptionWithContext
} from '../services/application-insights/appInsights';
import { 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../services/application-insights/appInsightsConstants';

export const useSessionSync = () => {

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const listenerAddedRef = useRef(false);
    const retryCountRef = useRef(0);
    const maxRetries = 3;

    const checkSessionStatus = async () => {
        try {
            await authApiV1.get(window.config.HORIZON_API_URL + '/token/status', { withCredentials: true });
            // Reset retry count on success
            retryCountRef.current = 0;
        } catch (error) {
            retryCountRef.current++;
            
            // Only log exceptions, not regular session checks
            trackExceptionWithContext(
                error instanceof Error ? error : new Error('Session check failed'),
                {
                    component: AppInsightsComponents.SESSION_SYNC_HOOK,
                    additionalData: {
                        errorType: AppInsightsErrorTypes.SESSION_SYNC_ERROR,
                        visibilityState: document.visibilityState,
                        retryCount: retryCountRef.current,
                        maxRetries
                    }
                }
            );

            // Implement exponential backoff for retries
            if (retryCountRef.current < maxRetries) {
                const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // 1s, 2s, 4s
                setTimeout(() => {
                    if (document.visibilityState === 'visible') {
                        checkSessionStatus();
                    }
                }, retryDelay);
            }
        }
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Check session when page becomes visible
                checkSessionStatus();
                
                // Start periodic session checking if not already active
                if (intervalRef.current === null) {
                    intervalRef.current = setInterval(() => {
                        if (document.visibilityState === 'visible') {
                            checkSessionStatus();
                        }
                    }, 5 * 60 * 1000); // 5 minutes
                }
            } else {
                // Stop interval when page becomes hidden
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        };

        // Initialize session sync on mount
        if (!listenerAddedRef.current) {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            listenerAddedRef.current = true;

            // Start initial session check if page is visible
            if (document.visibilityState === 'visible') {
                checkSessionStatus();
            }
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            listenerAddedRef.current = false;

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);
};
